import i18nFactory from '@yandex-int/i18n';
import { compact } from 'lodash';
import * as React from 'react';

import { GridCampaignNameCell } from '../../components/GridCampaignNameCell/GridCampaignNameCell';
import { GridESSStatusCellContainer } from '../../components/GridESSStatusCell/GridESSStatusCell';
import { GridESSStatusCellJSONContainer } from '../../components/GridESSStatusCellJSON/GridESSStatusCellJSON';
import { GridGroupNameCell } from '../../components/GridGroupNameCell/GridGroupNameCell';
import { GridGroupStatusCell } from '../../components/GridGroupStatusCell/GridGroupStatusCell';
import { GridGroupTypeCell } from '../../components/GridGroupTypeCell/GridGroupTypeCell';
import { GridActionsMenuContainer } from '../../containers/GridActionsMenuContainer/GridActionsMenuContainer';
import { addSettingsToDecl, arrangeInOrder } from '../../service/DeclarationHelper';
import { isStatusTextVisible } from '../../service/GridHelpers/GridHelpers';
import { ColumnsSettings, ColumnsSettingsViewDict, groups } from '../../typings/common';
import { DocCenterColumnTooltip } from '../../typings/doc-center';
import { Group } from '../../typings/domain';
import { CurrencyCode } from '../../typings/graphql-grid';
import {
    GridCellProps,
    GridColumnDecl,
    GridColumnsOptions,
    GridGroupItemActions,
    GridScreenTotalKey,
    OptionalGridColumnDecl,
    SortDirection
} from '../../typings/grid';
import { Recommendation, RecommendationType } from '../../typings/recommendations';
import { ClientFeatures } from '../../typings/state';
import { getDefaultGoalsViewInfo, getGoalsColumnsDecl } from '../goals/goals';
import { getDynamicStatsColumnsFields, getStaticStatsColumns } from '../stats/stats';

import * as keyset from './group.i18n';

const i18n = i18nFactory(keyset);

export enum GridGroupColumnKey {
    settings = 'settings',
    type = 'type',
    name = 'name',
    status = 'status_primaryStatus',
    campaignId = 'campaign_id',
    aggregatedStatus = 'aggregated-status',
    aggregatedStatusInfo = 'aggregated-status-info'
}

function getFixedGroupColumns(
    isCompactViewEnabled: boolean,
    features: ClientFeatures,
    clientLogin?: string
): Array<OptionalGridColumnDecl<Group, GridGroupItemActions, GridColumnsOptions>> {
    return [
        {
            title: '',
            key: GridGroupColumnKey.settings,
            cellClass: 'settings-cell',
            isSortable: false,
            mandatory: true,
            fixed: true,
            minWidth: 35,
            maxWidth: 35,
            CellComponent: React.memo(
                ({ rowIndex, actions }: GridCellProps<Group, GridGroupItemActions>): JSX.Element | null => {
                    if (!clientLogin) {
                        return null;
                    }

                    return <GridActionsMenuContainer index={rowIndex} actions={actions} />;
                }
            ),
            isAlignedToLeft: true
        },
        {
            title: i18n('type'),
            key: GridGroupColumnKey.type,
            isSortable: true,
            defaultSortDirection: SortDirection.asc,
            minWidth: 35,
            maxWidth: 65,
            align: 'right',
            fixed: true,
            tooltipKey: DocCenterColumnTooltip.campaignType,
            CellComponent: React.memo(
                ({
                    rowData: {
                        type,
                        campaign: { type: campaignType }
                    }
                }: GridCellProps<Group, GridGroupItemActions>): JSX.Element => {
                    return <GridGroupTypeCell type={type} campaignType={campaignType} features={features} />;
                }
            )
        },
        {
            title: i18n('name'),
            key: GridGroupColumnKey.name,
            fixed: true,
            mandatory: true,
            totalKey: GridScreenTotalKey.name,
            isAlignedToLeft: true,
            minWidth: 150,
            maxWidth: 600,
            CellComponent: React.memo(
                ({ rowData: group, actions }: GridCellProps<Group, GridGroupItemActions>): JSX.Element => {
                    const { name, id: groupId, recommendations, groupUrl } = group;

                    let recommendationsParams;

                    if (recommendations.data) {
                        recommendationsParams = {
                            items: recommendations,
                            onOpenRecommendationsPopup: actions.onOpenRecommendationsPopup,
                            onSubmitRecommendation: actions.onSubmitRecommendation,
                            onShowRecommendation: actions.onShowRecommendation
                        };
                    }

                    return (
                        <GridGroupNameCell
                            name={name}
                            id={groupId}
                            groupUrl={groupUrl}
                            recommendations={recommendationsParams}
                            isCompactViewEnabled={isCompactViewEnabled}
                        />
                    );
                }
            )
        }
    ];
}

function getStaticGroupColumns(
    options: GridColumnsOptions,
    isCompactViewEnabled: boolean,
    columnsSettings: ColumnsSettings,
    isAggregatedStatusAllowed: boolean
): Array<OptionalGridColumnDecl<Group, GridGroupItemActions, GridColumnsOptions>> {
    return compact([
        {
            title: i18n('status'),
            key: GridGroupColumnKey.status,
            isSortable: false,
            isAlignedToLeft: true,
            minWidth: 35,
            maxWidth: 300,
            CellComponent: React.memo(
                ({
                    rowData: {
                        status,
                        rejectReasons,
                        id,
                        recommendations: { data = [] },
                        campaign
                    },
                    actions
                }: GridCellProps<Group, GridGroupItemActions>): JSX.Element => {
                    const hasChangeAdGroupForModerationRecommendation = data.some(
                        (el: Recommendation) => el.type === RecommendationType.changeAdGroupForModeration
                    );

                    const columnViewInfo = columnsSettings.columnViewInfo[GridGroupColumnKey.status];

                    return (
                        <GridGroupStatusCell
                            campaignType={campaign.type}
                            groupId={id}
                            status={status}
                            rejectReasons={rejectReasons}
                            lang={options.currentLang}
                            tld={options.tld}
                            isOperatorAgency={options.operator.isAgency}
                            isOperatorSelfAgency={options.operator.isSelfAgency}
                            clientRegion={options.clientRegion}
                            getModerationRejectReasons={actions.getModerationRejectReasons}
                            hasChangeAdGroupForModerationRecommendation={hasChangeAdGroupForModerationRecommendation}
                            isTextVisible={isStatusTextVisible(isCompactViewEnabled, columnViewInfo)}
                        />
                    );
                }
            )
        },
        isAggregatedStatusAllowed && {
            title: 'New status',
            key: GridGroupColumnKey.aggregatedStatus,
            isSortable: false,
            minWidth: 35,
            maxWidth: 1000,
            CellComponent: React.memo(
                ({ rowData: { aggregatedStatus } }: GridCellProps<Group, GridGroupItemActions>): JSX.Element => {
                    return <GridESSStatusCellJSONContainer aggregatedStatus={aggregatedStatus} />;
                }
            )
        },
        isAggregatedStatusAllowed && {
            title: 'New status2',
            key: GridGroupColumnKey.aggregatedStatusInfo,
            isSortable: false,
            minWidth: 35,
            maxWidth: 300,
            CellComponent: React.memo(
                ({
                    rowData: { aggregatedStatusInfo }
                }: GridCellProps<Group, GridGroupItemActions>): JSX.Element | null => {
                    if (!aggregatedStatusInfo) {
                        return null;
                    }

                    return (
                        <GridESSStatusCellContainer
                            status={aggregatedStatusInfo.status}
                            reasons={aggregatedStatusInfo.reasons}
                            counters={aggregatedStatusInfo.counters}
                        />
                    );
                }
            )
        },
        {
            title: i18n('campaign'),
            key: GridGroupColumnKey.campaignId,
            minWidth: 300,
            maxWidth: 600,
            isAlignedToLeft: true,
            CellComponent: React.memo(
                ({ rowData: group }: GridCellProps<Group, GridGroupItemActions>): JSX.Element => {
                    const { name, id } = group.campaign;

                    return <GridCampaignNameCell name={name} id={id} />;
                }
            )
        }
    ]);
}

export const groupsColumnOrder = [
    'settings',
    'type',
    'name',
    'status_primaryStatus',
    'aggregated-status',
    'aggregated-status-info',
    'campaign_id',
    'GOALS',
    'STAT_COST',
    'STAT_GOALS',
    'STAT_REVENUE',
    'STAT_PROFITABILITY',
    'STAT_AVG_GOAL_COST',
    'STAT_AVG_CLICK_COST',
    'STAT_SHOWS',
    'STAT_CPM_PRICE',
    'STAT_CLICKS',
    'STAT_CTR',
    'STAT_CONVERSION_RATE',
    'STAT_BOUNCE_RATE'
];

export function getDefaultDynamicGroupColumnsFields(): ColumnsSettings {
    return {
        columnOrder: groupsColumnOrder,
        columnViewInfo: {
            settings: {
                width: 35,
                widthCompact: 35,
                hidden: false
            },
            name: {
                width: 250,
                widthCompact: 250,
                hidden: false
            },
            type: {
                width: 35,
                widthCompact: 35,
                hidden: false
            },
            status_primaryStatus: {
                width: 200,
                widthCompact: 200,
                hidden: false
            },
            'aggregated-status': {
                width: 250,
                widthCompact: 250,
                hidden: false
            },
            'aggregated-status-info': {
                width: 250,
                widthCompact: 250,
                hidden: false
            },
            campaign_id: {
                width: 300,
                widthCompact: 300,
                hidden: false
            },
            ...getDefaultGoalsViewInfo(),
            ...getDynamicStatsColumnsFields()
        }
    };
}

export function getGroupColumns(
    options: GridColumnsOptions,
    columnsSettings: ColumnsSettingsViewDict,
    clientCurrency: CurrencyCode,
    isCompactViewEnabled: boolean,
    isGoalsStatEnabled: boolean,
    hasSelectedGoals: boolean,
    isAggregatedStatusAllowed: boolean,
    features: ClientFeatures,
    clientLogin?: string,
    hasSelectedGoalsAtColumnsSettingsEditor?: boolean,
    isColumnsSettingsEditorLocation?: boolean
): Array<GridColumnDecl<Group>> {
    const fixedColumns = getFixedGroupColumns(isCompactViewEnabled, features, clientLogin).map(column => {
        return addSettingsToDecl(column, columnsSettings, groups);
    });

    const result = getStaticGroupColumns(
        options,
        isCompactViewEnabled,
        columnsSettings[groups],
        isAggregatedStatusAllowed
    )
        .concat(getGoalsColumnsDecl<Group>(hasSelectedGoalsAtColumnsSettingsEditor, isColumnsSettingsEditorLocation))
        .concat(
            getStaticStatsColumns<Group>(
                {
                    getCurrency: (item: Group): CurrencyCode => item.campaign.currency
                },
                clientCurrency,
                isCompactViewEnabled,
                hasSelectedGoals,
                isGoalsStatEnabled,
                hasSelectedGoalsAtColumnsSettingsEditor,
                isColumnsSettingsEditorLocation
            )
        )
        .filter(column => {
            return column.isAvailable ? column.isAvailable(options) : true;
        })
        .map(column => {
            return addSettingsToDecl(column, columnsSettings, groups);
        });

    return fixedColumns.concat(arrangeInOrder(result, columnsSettings[groups].columnOrder, true));
}
